import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Warehouse, Building2, Home, Shield, Snowflake, Zap,
  ChevronDown, ChevronUp, CheckCircle2
} from 'lucide-react';

const ServicesSection = () => {
  const { language } = useLanguage();
  const [expandedService, setExpandedService] = useState(0);

  const content = {
    fr: {
      badge: 'NOS SERVICES',
      title: 'Expertise complète',
      subtitle: 'Une solution globale pour tous vos projets',
      services: [
        {
          icon: Warehouse,
          title: 'Construction Métallique',
          subtitle: 'Notre cœur de métier',
          isPrimary: true,
          description: 'Conception, fabrication et montage de structures métalliques pour tous types de projets industriels.',
          features: [
            'Charpentes métalliques pour bâtiments industriels et entrepôts',
            'Structures pour ateliers, hangars et plateformes techniques',
            'Ouvrages secondaires : escaliers, garde-corps, passerelles',
            'Menuiserie Aluminium & Inox : portes, fenêtres, façades',
            'Alucobond : habillage de façades moderne',
            'Renforcement et réhabilitation de structures existantes'
          ]
        },
        {
          icon: Building2,
          title: 'Bâtiment & Travaux Publics',
          subtitle: 'BTP',
          description: 'Construction et rénovation de bâtiments résidentiels, commerciaux et industriels.',
          features: [
            'Études architecturales et techniques',
            'Construction de bâtiments neufs',
            'Réhabilitation et modernisation',
            'Extension de structures existantes'
          ]
        },
        {
          icon: Home,
          title: 'Gestion Immobilière',
          subtitle: 'Services complets',
          description: 'Administration et gestion de biens immobiliers pour particuliers et professionnels.',
          features: [
            'Administration de biens (gérance locative)',
            'Syndic de copropriété',
            'Transactions immobilières (achat, vente, location)'
          ]
        },
        {
          icon: Shield,
          title: 'Sécurité',
          subtitle: 'Résidentielle & Professionnelle',
          description: 'Solutions de sécurité complètes pour protéger vos biens et vos locaux.',
          features: [
            'Systèmes d\'alarme et vidéosurveillance (CCTV)',
            'Contrôle d\'accès (interphones, digicodes, biométrie)',
            'Portails et clôtures sécurisés'
          ]
        },
        {
          icon: Snowflake,
          title: 'Froid & Climatisation',
          subtitle: 'Confort thermique',
          description: 'Installation et maintenance de systèmes de climatisation et réfrigération.',
          features: [
            'Installation de climatiseurs (split, centralisé, VRV)',
            'Entretien et dépannage',
            'Systèmes de réfrigération'
          ]
        },
        {
          icon: Zap,
          title: 'Électricité Bâtiment',
          subtitle: 'Solutions électriques',
          description: 'Installations électriques complètes pour tous types de bâtiments.',
          features: [
            'Installation électrique (courants forts et faibles)',
            'Mise aux normes et rénovation',
            'Maintenance et dépannage'
          ]
        }
      ]
    },
    en: {
      badge: 'OUR SERVICES',
      title: 'Complete expertise',
      subtitle: 'A global solution for all your projects',
      services: [
        {
          icon: Warehouse,
          title: 'Metal Construction',
          subtitle: 'Our core business',
          isPrimary: true,
          description: 'Design, fabrication and assembly of metal structures for all types of industrial projects.',
          features: [
            'Metal frameworks for industrial buildings and warehouses',
            'Structures for workshops, hangars and technical platforms',
            'Secondary works: stairs, railings, walkways',
            'Aluminum & Stainless Steel joinery: doors, windows, facades',
            'Alucobond: modern facade cladding',
            'Reinforcement and rehabilitation of existing structures'
          ]
        },
        {
          icon: Building2,
          title: 'Building & Public Works',
          subtitle: 'BTP',
          description: 'Construction and renovation of residential, commercial and industrial buildings.',
          features: [
            'Architectural and technical studies',
            'New building construction',
            'Rehabilitation and modernization',
            'Extension of existing structures'
          ]
        },
        {
          icon: Home,
          title: 'Real Estate Management',
          subtitle: 'Complete services',
          description: 'Property administration and management for individuals and professionals.',
          features: [
            'Property administration (rental management)',
            'Condominium management',
            'Real estate transactions (purchase, sale, rental)'
          ]
        },
        {
          icon: Shield,
          title: 'Security',
          subtitle: 'Residential & Professional',
          description: 'Complete security solutions to protect your property and premises.',
          features: [
            'Alarm systems and video surveillance (CCTV)',
            'Access control (intercoms, digital codes, biometrics)',
            'Secure gates and fences'
          ]
        },
        {
          icon: Snowflake,
          title: 'HVAC & Refrigeration',
          subtitle: 'Thermal comfort',
          description: 'Installation and maintenance of air conditioning and refrigeration systems.',
          features: [
            'Air conditioner installation (split, centralized, VRV)',
            'Maintenance and repair',
            'Refrigeration systems'
          ]
        },
        {
          icon: Zap,
          title: 'Building Electrical',
          subtitle: 'Electrical solutions',
          description: 'Complete electrical installations for all types of buildings.',
          features: [
            'Electrical installation (high and low current)',
            'Upgrading and renovation',
            'Maintenance and troubleshooting'
          ]
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227]/10 border border-[#C9A227]/20 rounded-full mb-6">
            <span className="text-[#C9A227] font-semibold text-sm">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">{t.subtitle}</p>
        </div>

        {/* Services List */}
        <div className="space-y-4">
          {t.services.map((service, index) => {
            const Icon = service.icon;
            const isExpanded = expandedService === index;
            const isPrimary = service.isPrimary;
            
            return (
              <div 
                key={index}
                className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                  isPrimary 
                    ? 'bg-[#1A1A1A] text-white' 
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                {/* Header - Always visible */}
                <button
                  onClick={() => setExpandedService(isExpanded ? -1 : index)}
                  className={`w-full p-6 flex items-center justify-between ${
                    isPrimary ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-100'
                  } transition-colors`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      isPrimary 
                        ? 'bg-[#C9A227]' 
                        : 'bg-white border border-gray-200'
                    }`}>
                      <Icon className={`w-7 h-7 ${isPrimary ? 'text-white' : 'text-[#1A1A1A]'}`} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-3">
                        <h3 className={`text-xl font-bold ${isPrimary ? 'text-white' : 'text-[#1A1A1A]'}`}>
                          {service.title}
                        </h3>
                        {isPrimary && (
                          <span className="px-2 py-1 bg-[#C9A227] text-white text-xs font-semibold rounded">
                            {language === 'fr' ? 'PRINCIPAL' : 'MAIN'}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isPrimary ? 'text-[#C9A227]' : 'text-[#C9A227]'}`}>
                        {service.subtitle}
                      </p>
                    </div>
                  </div>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isPrimary ? 'bg-[#2A2A2A]' : 'bg-white border border-gray-200'
                  }`}>
                    {isExpanded 
                      ? <ChevronUp className={`w-5 h-5 ${isPrimary ? 'text-white' : 'text-gray-600'}`} />
                      : <ChevronDown className={`w-5 h-5 ${isPrimary ? 'text-white' : 'text-gray-600'}`} />
                    }
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className={`px-6 pb-6 ${isPrimary ? 'border-t border-[#2A2A2A]' : 'border-t border-gray-200'}`}>
                    <div className="pt-6">
                      <p className={`mb-6 ${isPrimary ? 'text-gray-300' : 'text-gray-600'}`}>
                        {service.description}
                      </p>
                      <div className="grid md:grid-cols-2 gap-3">
                        {service.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-start gap-3">
                            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                              isPrimary ? 'text-[#C9A227]' : 'text-[#C9A227]'
                            }`} />
                            <span className={`text-sm ${isPrimary ? 'text-gray-300' : 'text-gray-600'}`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;